import { useEffect } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, Clock, MapPin, Users } from "lucide-react";
import { Link } from "wouter";
import { Expandable } from "../components/ux/Expandable";
import Logo from "@/components/Logo";
import { useT } from "@/lib/i18n";
import tracking from "@/lib/tracking";
import { quizHref, StickyCta } from "./shared";

/**
 * "Reservation confirmed" landing layout: the page is a booking confirmation
 * that already exists, with one unclaimed seat. For audiences whose ache is
 * that nobody invites them anymore, the invitation has already happened.
 * Single CTA: claim the seat. MCP template name: "reservation".
 */

export interface ReservationConfig {
  posthogEvent: string;
  audienceParam: string;
  eventTypeParam?: string;
  confirmedLabel: string;
  headline: string;
  subline: string;
  details: Array<{ icon: "date" | "time" | "place" | "party"; label: string; value: string }>;
  seats: { total: number; unclaimedLabel: string };
  cta: string;
  ctaSub: string;
  reassure: { quote: string; author: string };
  faqTitle: string;
  faq: Array<{ q: string; a: string }>;
  ownedLabel?: "gay" | "lgbtq";
}

const ICONS = { date: CalendarCheck, time: Clock, place: MapPin, party: Users };

export default function LandingReservation({ config }: { config: ReservationConfig }) {
  const { t: tRaw } = useT("landing");

  useEffect(() => {
    tracking.trackPage(config.posthogEvent);
  }, [config.posthogEvent]);

  const ctaHref = quizHref(config.audienceParam, config.eventTypeParam);

  return (
    <div className="min-h-full bg-neutral-100 dark:bg-neutral-900 py-8 px-4 pb-28">
      <div className="max-w-md mx-auto">
        <div className="flex justify-center mb-6">
          <Logo withIcon={true} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white dark:bg-neutral-800 rounded-3xl shadow-xl overflow-hidden"
        >
          {/* confirmation banner */}
          <div className="bg-emerald-500/10 border-b border-emerald-500/30 px-6 py-3 flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">
              {config.confirmedLabel}
            </p>
          </div>

          <div className="px-6 py-6">
            <h1 className="text-2xl font-semibold text-type-950 dark:text-neutral-50 font-Poppins">
              {config.headline}
            </h1>
            <p className="text-dark-muted dark:text-neutral-400 mt-2">{config.subline}</p>

            {/* details */}
            <div className="mt-6 flex flex-col gap-3">
              {config.details.map((d, i) => {
                const Icon = ICONS[d.icon];
                return (
                  <div key={i} className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-primary-500 shrink-0" />
                    <span className="text-sm text-neutral-400 w-24 shrink-0">{d.label}</span>
                    <span className="text-sm font-medium text-type-950 dark:text-neutral-100">
                      {d.value}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* seats */}
            <div className="mt-6 border-t border-neutral-200 dark:border-neutral-700 pt-5">
              <div className="flex items-center gap-2">
                {/* group size varies; the trailing faded dot keeps the count non-literal */}
                {Array.from({ length: config.seats.total }, (_, i) => {
                  const unclaimed = i === config.seats.total - 1;
                  return (
                    <div
                      key={i}
                      className={
                        unclaimed
                          ? "w-9 h-9 rounded-full border-2 border-dashed border-primary-500 flex items-center justify-center text-primary-500 font-bold animate-pulse"
                          : "w-9 h-9 rounded-full bg-neutral-300 dark:bg-neutral-600"
                      }
                    >
                      {unclaimed ? "?" : ""}
                    </div>
                  );
                })}
                <div className="w-9 h-9 rounded-full bg-neutral-200 dark:bg-neutral-700 opacity-50 flex items-center justify-center text-neutral-400 text-xs font-bold">
                  +
                </div>
              </div>
              <p className="text-sm text-primary-500 font-semibold mt-3">
                {config.seats.unclaimedLabel}
              </p>
            </div>

            {/* CTA */}
            <a
              href={ctaHref}
              className="mt-6 rounded-full bg-primary-500 hover:bg-primary-600 text-white font-bold text-lg py-4 w-full inline-flex items-center justify-center transition-colors shadow-lg shadow-primary-500/30"
            >
              {config.cta}
            </a>
            <p className="text-sm text-dark-muted dark:text-neutral-400 text-center mt-3">
              {config.ctaSub}
            </p>
          </div>
        </motion.div>

        {/* reassurance */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="bg-white dark:bg-neutral-800 rounded-2xl p-5 mt-6 border border-neutral-200 dark:border-neutral-700"
        >
          <p className="text-dark-muted dark:text-neutral-300 italic leading-relaxed">
            “{config.reassure.quote}”
          </p>
          <p className="text-sm font-semibold text-neutral-400 mt-2">{config.reassure.author}</p>
        </motion.div>

        {/* FAQ */}
        <div className="mt-8">
          <p className="text-center text-xs uppercase tracking-widest text-neutral-400 mb-4">
            {config.faqTitle}
          </p>
          <div className="flex flex-col gap-3">
            {config.faq.map((f, i) => (
              <Expandable key={i} title={f.q}>
                {f.a}
              </Expandable>
            ))}
          </div>
        </div>

        {/* footer */}
        <div className="mt-10 text-center text-xs text-neutral-400 dark:text-neutral-600">
          <p className="flex items-center justify-center gap-1.5 mb-2">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-red-500 via-yellow-400 to-violet-500" />
            {tRaw(config.ownedLabel === "lgbtq" ? "footer.proudlyLgbtqOwned" : "footer.proudlyGayOwned")}
          </p>
          <p>
            <Link href="/terms" className="underline hover:text-primary-500 transition-colors">
              {tRaw("footer.terms")}
            </Link>
            {" · "}
            <Link href="/privacy" className="underline hover:text-primary-500 transition-colors">
              {tRaw("footer.privacy")}
            </Link>
          </p>
        </div>
      </div>

      <StickyCta href={ctaHref} label={config.cta} />
    </div>
  );
}

/** Example config, rendered at /tpl/reservation and documented for MCP authors. */
export const RESERVATION_EXAMPLE: ReservationConfig = {
  posthogEvent: "tpl_reservation_preview",
  audienceParam: "m",
  confirmedLabel: "Reservation confirmed",
  headline: "Your table is booked for Thursday.",
  subline: "A table of gay men at your stage of life, matched by personality and social energy. We made the reservation already. One seat isn't claimed yet.",
  details: [
    { icon: "date", label: "Date", value: "Thursday" },
    { icon: "time", label: "Time", value: "7:00 PM" },
    { icon: "place", label: "Place", value: "A great spot near you. We pick it, we book it." },
    { icon: "party", label: "Party", value: "Gay men on your wavelength" },
  ],
  seats: { total: 6, unclaimedLabel: "1 seat unclaimed. It's yours if you want it." },
  cta: "Claim your seat",
  ctaSub: "Take the five minute quiz. We handle everything else.",
  reassure: {
    quote: "Honestly was skeptical. Dinner with strangers sounds like a team building exercise. But the guys at my table were genuinely cool and we ended up closing the bar after. Thursday's my favorite night now.",
    author: "Kyle, 31, San Francisco",
  },
  faqTitle: "Before you claim it",
  faq: [
    { q: "Is this a dating thing?", a: "No. Outclose is built for friendship. Tables are gay men looking for the same thing you are: people to actually know in your city." },
    { q: "What does it cost?", a: "$15.99 per dinner, or $19.99/month for weekly dinners and brunches plus perks. Food and drink at the restaurant are on you, like any night out." },
    { q: "What if I don't click with my table?", a: "After the dinner you mark who you'd want to stay in touch with. Mutual matches unlock contacts. Try a different table next week." },
  ],
  ownedLabel: "gay",
};
